import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  addDiallerGroupCampaign,
  getDiallerGroupCampaigns,
  getDiallerGroupCampaignsByNextUrl,
  removeCampaignFromGroup,
  updateDiallerGroupCampaign,
} from './api';
import {
  AddCampaignGroupAssignment,
  DiallerGroupCampaign,
  DiallerGroupCampaignsResponse,
  DiallerGroupFilterType,
  UpdateCampaignGroupAssignment,
} from './domain';

type Options = {
  search?: string;
  filters?: DiallerGroupFilterType[];
  shouldFetch?: boolean;
};

const useDiallerGroupCampaignSearch = (diallerGroupId: number, options: Options = {}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<DiallerGroupCampaign[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const { search, filters, shouldFetch = true } = options;
  const stringFilters = JSON.stringify(filters);

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: DiallerGroupCampaignsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getDiallerGroupCampaignsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.campaigns]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: DiallerGroupCampaignsResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getDiallerGroupCampaigns(diallerGroupId, search, filters, axiosCancelRef.current);
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.campaigns);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [diallerGroupId, search, stringFilters]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  const add = useCallback(
    async (data: AddCampaignGroupAssignment) => {
      try {
        await addDiallerGroupCampaign(diallerGroupId, data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [diallerGroupId],
  );

  const update = useCallback(
    async (data: UpdateCampaignGroupAssignment) => {
      try {
        await updateDiallerGroupCampaign(diallerGroupId, data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [diallerGroupId],
  );

  const remove = useCallback(
    async (entryId: number) => {
      try {
        await removeCampaignFromGroup(diallerGroupId, entryId);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [diallerGroupId],
  );

  useEffect(() => {
    setList([]);
  }, [diallerGroupId, search, stringFilters, shouldFetch]);

  useEffect(() => {
    const load = async (
      diallerGroupId: number,
      search: string | undefined,
      filters: DiallerGroupFilterType[] | undefined,
    ) => {
      setLoading(true);
      setError(false);

      let resp: DiallerGroupCampaignsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getDiallerGroupCampaigns(diallerGroupId, search, filters, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.campaigns]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(diallerGroupId, search, filters);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [diallerGroupId, search, stringFilters, shouldFetch]);

  return { loading, error, list, hasMore, add, update, remove, intersectionObserverRef };
};

export default useDiallerGroupCampaignSearch;
